import React, { FC, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { CreateAndCloseLinkedApi } from "../../../services/Api/Organization/types";
import { api } from "../../../services/Api/api";

interface Props {
	title: string;
	onHide: () => void;
	isShown: boolean;
	onGoBack: () => void;
	form: UseFormReturn<CreateAndCloseLinkedApi>;
	linkedId?: number;
	onSuccess: (response: any) => void;
	resourceType: "CONTACT" | "PRODUCT";
}

const CreateEntityModalForm: FC<Props> = ({
	title,
	form,
	onGoBack,
	isShown,
	onHide,
	linkedId,
	onSuccess,
	resourceType,
	...props
}) => {
	const { formState, handleSubmit, setError } = form;
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const [loading, setLoading] = useState<boolean>(false);

	const onParseData = (data: CreateAndCloseLinkedApi) => {
		if (data.type === "BASIC") {
			data.components = linkedId ? undefined : [];
			data.recipe_amount = undefined;
		}
		if (data.type === "PACKAGE") {
			data.components = data?.components?.filter(
				(item) => item?.product_id || (!isNaN(item?.recipe_amount) && item.recipe_amount !== null)
			);
		}

		return data;
	};

	const onSubmit = handleSubmit(async (data: CreateAndCloseLinkedApi) => {
		setLoading(true);
		try {
			let res;
			let newData = onParseData(data);
			if (linkedId) {
				res = await api.organization().createAndCloseIntegrationLink(linkedId, newData);
			} else {
				if (resourceType === "PRODUCT") res = await api.organization().createLinkedProduct(newData);
				if (resourceType === "CONTACT") {
					newData = { ...newData, item_name: data.name };
					res = await api.organization().createLinkedContact(newData);
				}
			}
			onSuccess(Array.isArray(res) ? res[0] : res);
			addSuccessFlash(t("lib:common.flash.completed"));
			onHide();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<Modal onHide={onHide} show={isShown} size="lg">
			<FormDirty formState={formState} key="create-entity-modal-form">
				<Modal.Header closeButton>
					<Modal.Title>{title}</Modal.Title>
				</Modal.Header>
				<Modal.Body>{props.children}</Modal.Body>
				<Modal.Footer>
					<Button variant="light" onClick={onGoBack} className="me-auto">
						{t("modules.app_link.action.go_back.title")}
					</Button>
					<div>
						<ButtonLoading loading={loading} type="submit" onClick={onSubmit} className="me-2">
							{t("lib:common.action.save")}
						</ButtonLoading>
						<Button variant="light" onClick={onHide}>
							{t("lib:common.action.cancel")}
						</Button>
					</div>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default CreateEntityModalForm;
